import { Attachment, canEditAttachment, Organization, User } from '@ee/common/models';
import { Permission } from '@ee/common/enums';

/**
 * Whether this user may fill in a PDF's AcroForm fields and save the result.
 *
 * The premium FLAG says the product is available to this org; the SUBSCRIPTION says it is currently
 * paid for. Both are required — a lapsed subscription must fall back to a read-only preview rather
 * than a broken editor. [efilingSubscribed] is passed IN rather than resolved here: it costs an HTTP
 * round-trip, and a dialog that fired a billing request on every document preview would flicker its
 * own buttons. The case screen already resolves it once and hands it down.
 *
 * `EFILING_SUBMIT` rather than `EFILING_VIEW`: filling a court form is writing to a document that gets
 * filed, so a read-only e-filing viewer must not be handed an editable one.
 *
 * It ALSO requires document edit on this attachment's kind. Saving a filled form changes an existing
 * document, so the server gates it on `case:{upload,generate}-documents:edit` exactly as it gates a
 * rename. Without this check an e-filer lacking that permission got editable fields and a SAVE that
 * 403s — after the 2-90s upload scan, with their typing lost.
 *
 * This is a UX gate. The enforcement is the endpoint's own `@PreAuthorize` plus its org
 * premium-feature and per-kind edit checks — a client that lies gets a 403, not a saved document.
 */
export function canFillPdfForms(
  org?: Organization | null,
  user?: User | null,
  attachment?: Attachment | null,
  efilingSubscribed?: boolean
): boolean {
  return !!efilingSubscribed
    && !!org?.premium_features?.['EFILING']
    && qualifiesToFillPdfForms(org, user, attachment);
}

/**
 * Everything [canFillPdfForms] requires EXCEPT the subscription.
 *
 * This is what decides whether to show the disabled SAVE teaser and its explanation: someone who
 * would be able to fill this form if the add-on were bought. Deliberately still requires the
 * document-edit permission, so the upsell reaches people who could actually use the feature rather
 * than everyone who opens a court form — a client-portal user, or a paralegal whose admin has not
 * granted edit, is not who an add-on pitch is for.
 *
 * It does NOT require the EFILING premium flag or the subscription: those are the two things the
 * add-on turns on, so requiring them would hide the pitch from exactly the orgs it is for.
 */
export function qualifiesToFillPdfForms(
  org?: Organization | null,
  user?: User | null,
  attachment?: Attachment | null
): boolean {
  return !!user?.permissions?.includes(Permission.EFILING_SUBMIT)
    && canEditAttachment(user?.permissions, attachment);
}
